import { IconLogin } from "@/assets/icons/IconLogin";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { RESEND_COOLDOWN_SECONDS, RESEND_VERIFY_CODE_COOLDOWN_STORAGE_KEY } from "@/constants/common";
import { verifyLoginSchema } from "@/features/auth/schema/verify-login-schema";
import { resendLoginVerifyCodeApi } from "@/features/auth/services/resend-login-verify-code-api";
import { verifyLoginApi } from "@/features/auth/services/verify-login-api";
import { black, text } from "@/theme/colors";
import { getInitialCooldownOnReload } from "@/utils/cooldown";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { getSafeRedirectURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Paper, Stack, Typography, styled } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

type TLoginVerifyLocationState = {
  email: string;
  pendingAuthToken: string;
  redirectUrl?: string;
};

type TLoginVerifyFormValues = {
  email?: string;
  code: string;
};

export const LoginVerifyForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as TLoginVerifyLocationState | null;

  const email = state?.email ?? "";
  const pendingAuthToken = state?.pendingAuthToken ?? "";

  const [resendCooldown, setResendCooldown] = useState(() => getInitialCooldownOnReload(RESEND_VERIFY_CODE_COOLDOWN_STORAGE_KEY));
  const [isResending, setIsResending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      localStorage.removeItem(RESEND_VERIFY_CODE_COOLDOWN_STORAGE_KEY);
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      localStorage.removeItem(RESEND_VERIFY_CODE_COOLDOWN_STORAGE_KEY);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          localStorage.removeItem(RESEND_VERIFY_CODE_COOLDOWN_STORAGE_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendCooldown > 0]);

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    try {
      await resendLoginVerifyCodeApi({ email, pendingAuthToken });
      toast.success("認証コードを再送しました");
      const cooldownUntil = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      localStorage.setItem(RESEND_VERIFY_CODE_COOLDOWN_STORAGE_KEY, String(cooldownUntil));
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      showError(error);
    } finally {
      setIsResending(false);
    }
  }, [email, pendingAuthToken, resendCooldown, isResending]);

  const methods = useForm<TLoginVerifyFormValues>({
    defaultValues: { email, code: "" },
    mode: "all",
    reValidateMode: "onBlur",
    resolver: yupResolver(verifyLoginSchema),
  });
  const { control, formState } = methods;

  const onSubmit = async (data: TLoginVerifyFormValues) => {
    try {
      await verifyLoginApi({
        ...data,
        email: data?.email ?? "",
        pendingAuthToken,
      });
      navigate(getSafeRedirectURL(state?.redirectUrl));
    } catch (error) {
      showError(error);
    }
  };

  const isFormValid = !formState.isSubmitting;

  return (
    <Container
      elevation={0}
      sx={{
        width: 464,
        p: 4,
        borderRadius: "16px",
      }}
    >
      <Stack>
        <Typography variant="head20Bold" color={black[30]} textAlign="center" mb="32px">
          二段階認証
        </Typography>
        <Typography fontSize="14px" color={text.secondary} mb="28px">
          メールへ送付された認証コードを入力してください。
        </Typography>
        <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={verifyLoginSchema}>
          <Stack gap="24px">
            <FormField control={control} name="email" label="メールアドレス" required={false}>
              <Input size="lg" placeholder="example@iro-hana.com" readonly />
            </FormField>
            <FormField control={control} name="code" label="認証コード" required>
              <Input size="lg" />
            </FormField>
            <Stack flexDirection="column" gap={2}>
              <Button
                type="submit"
                fullWidth
                startIcon={<IconLogin color="currentColor" />}
                disabled={!isFormValid}
                sx={{
                  mt: "4px",
                  height: "48px !important",
                  borderRadius: "8px",
                  "&.Mui-disabled": {
                    backgroundColor: "#E0E0E0",
                    color: "#9E9E9E",
                  },
                }}
              >
                <Typography variant="body14Semi">ログイン</Typography>
              </Button>
              <Button type="button" size="sm" variant="ghost" fullWidth disabled={resendCooldown > 0 || isResending} onClick={handleResendCode}>
                {resendCooldown > 0 ? `認証コードを再送する（${resendCooldown}秒）` : "認証コードを再送する"}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Stack>
    </Container>
  );
};

const Container = styled(Paper)`
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
